import type { StateStorage } from "../lib/storage";

const AUTH_CHANGE_STORAGE_KEY = "tiputini:account-change:v1";
const AUTH_CHANGE_CHANNEL = "tiputini:account-change:v1";

let activeAccountId: string | null | undefined;
let authChangeChannel: BroadcastChannel | undefined;
let watchingAuthChanges = false;
let authChangeRevision = 0;
let storageListener: ((event: StorageEvent) => void) | undefined;
let pageShowListener: ((event: PageTransitionEvent) => void) | undefined;

type AccountResponse = {
  account?: {
    id?: unknown;
  };
};

function accountScopeSegment(): string | undefined {
  if (activeAccountId === undefined) return undefined;
  return activeAccountId === null ? "anonymous" : encodeURIComponent(activeAccountId);
}

export function initializeTiputiniAccountScope(accountId: string | null): void {
  activeAccountId = accountId;
}

export function currentTiputiniAccountId(): string | null | undefined {
  return activeAccountId;
}

export function scopeTiputiniPersistenceKey(key: string): string {
  const segment = accountScopeSegment();
  return segment === undefined ? key : `tiputini:${segment}:${key}`;
}

export function scopeTiputiniDatabaseName(name: string): string {
  return scopeTiputiniPersistenceKey(name);
}

export function scopeTiputiniStorage(storage: StateStorage): StateStorage {
  return {
    getItem: (name) => storage.getItem(scopeTiputiniPersistenceKey(name)),
    setItem: (name, value) => storage.setItem(scopeTiputiniPersistenceKey(name), value),
    removeItem: (name) => storage.removeItem(scopeTiputiniPersistenceKey(name)),
  };
}

export async function prepareTiputiniAccountScope(
  fetcher: typeof fetch = fetch,
): Promise<string | null> {
  const response = await fetcher("/api/tiputini/account", { credentials: "same-origin" });
  if (response.status === 401) {
    initializeTiputiniAccountScope(null);
    return null;
  }
  if (!response.ok) {
    throw new Error(`Could not verify the Tiputini session (${response.status}).`);
  }
  const body = (await response.json()) as AccountResponse;
  const accountId = body.account?.id;
  if (typeof accountId !== "string" || accountId.length === 0) {
    throw new Error("The Tiputini session response did not include an account id.");
  }
  initializeTiputiniAccountScope(accountId);
  return accountId;
}

function parseAccountChange(raw: string | null): string | null | undefined {
  if (raw === null) return undefined;
  try {
    const parsed = JSON.parse(raw) as { accountId?: unknown };
    return parsed.accountId === null || typeof parsed.accountId === "string"
      ? parsed.accountId
      : undefined;
  } catch {
    return undefined;
  }
}

function handleAccountChange(accountId: string | null | undefined): void {
  if (accountId === undefined || accountId === activeAccountId) return;
  window.location.assign("/");
}

export function watchTiputiniAccountChanges(): void {
  if (watchingAuthChanges || typeof window === "undefined") return;
  watchingAuthChanges = true;
  storageListener = (event) => {
    if (event.key === AUTH_CHANGE_STORAGE_KEY)
      handleAccountChange(parseAccountChange(event.newValue));
  };
  pageShowListener = (event) => {
    if (event.persisted) window.location.assign("/");
  };
  window.addEventListener("storage", storageListener);
  window.addEventListener("pageshow", pageShowListener);
  if (typeof BroadcastChannel !== "undefined") {
    authChangeChannel = new BroadcastChannel(AUTH_CHANGE_CHANNEL);
    authChangeChannel.addEventListener("message", (event: MessageEvent<unknown>) => {
      handleAccountChange(
        parseAccountChange(
          typeof event.data === "string" ? event.data : JSON.stringify(event.data),
        ),
      );
    });
  }
}

export function reloadForTiputiniAccountChange(accountId: string | null, destination = "/"): void {
  const payload = JSON.stringify({ accountId, revision: ++authChangeRevision });
  try {
    window.localStorage.setItem(AUTH_CHANGE_STORAGE_KEY, payload);
  } catch {
    // BroadcastChannel remains available in browsers that block local storage.
  }
  authChangeChannel?.postMessage(payload);
  window.location.assign(destination);
}

export function resetTiputiniAccountScopeForTest(): void {
  activeAccountId = undefined;
  if (typeof window !== "undefined") {
    if (storageListener) window.removeEventListener("storage", storageListener);
    if (pageShowListener) window.removeEventListener("pageshow", pageShowListener);
  }
  storageListener = undefined;
  pageShowListener = undefined;
  authChangeChannel?.close();
  authChangeChannel = undefined;
  watchingAuthChanges = false;
  authChangeRevision = 0;
}
